import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {
  ConfigProvider, DatePicker, Select, Button, Space, Table, Tag, Card, Statistic, Row, Col,
  Typography, Alert, message, Grid,
} from 'antd';
import { LeftOutlined, RightOutlined, ReloadOutlined, DownloadOutlined, QrcodeOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import ko_KR from 'antd/es/locale/ko_KR';
import ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import '../styles/Common.css';
import { API_BASE } from '../config/api';
import WorkHoursMobile from '../components/WorkHoursMobile';
import QRCode from 'qrcode';
import EmployeeWorkHoursModal, { printQrCards, linkUrl } from '../components/EmployeeWorkHoursModal';
import { AuthContext } from '../contexts/AuthContext';

dayjs.locale('ko');

const { Text } = Typography;
const { Option } = Select;

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const WEEKDAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

const STATUS_META = {
  OVER: { color: 'red', label: '12시간 초과' },
  WARN: { color: 'orange', label: '주의 (10시간 이상)' },
  OK: { color: 'green', label: '정상' },
};

const mondayOf = (d) => {
  const day = d.day(); // 일=0 … 토=6
  return d.subtract((day + 6) % 7, 'day').startOf('day');
};

const hoursCell = (v) => (v ? v : <Text type="secondary">-</Text>);

const WorkHoursPage = () => {
  const [weekStart, setWeekStart] = useState(() => mondayOf(dayjs()));
  const [part, setPart] = useState('All');
  // 볼 수 있는 부서 목록은 서버 응답(scope)을 쓴다 — 일반 계정은 담당 부서만
  const [scope, setScope] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailNo, setDetailNo] = useState(null); // 상세 팝업 대상 사번
  const [printing, setPrinting] = useState(false);
  const { auth } = useContext(AuthContext);
  const yes = (v) => v === 1 || v === '1' || v === true;
  // QR 일괄 인쇄는 야근관리 '수정' 또는 권한관리 권한 (서버도 같은 기준)
  const canManageLink = (auth?.menuPermissions || []).some(
    (p) => (p.menu_key === 'overtime' && yes(p.can_update)) || (p.menu_key === 'auth-admin' && yes(p.can_view))
  );
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === undefined ? window.innerWidth < 768 : !screens.md;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/api/work-hours/weekly', {
        params: { date: weekStart.format('YYYY-MM-DD'), part },
      });
      const ok = res && res.success && Array.isArray(res.departments);
      setData(ok ? res : null);
      if (ok && res.scope) setScope(res.scope);
    } catch (e) {
      setData(null);
      message.error(e.response?.data?.message || '근무시간 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [weekStart, part]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const days = useMemo(() => data?.days || [], [data]);
  const holidaySet = useMemo(() => new Set((data?.holidays || []).map((h) => h.d)), [data]);

  const employeeColumns = useMemo(() => [
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 110,
      render: (v, r) => <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setDetailNo(r.employee_no)}>{v}</Button>,
    },
    { title: '직급', dataIndex: 'rank', key: 'rank', width: 70, render: (v) => v || '-' },
    {
      title: '야근+조근',
      dataIndex: 'checked_hours',
      key: 'checked_hours',
      width: 90,
      sorter: (a, b) => a.checked_hours - b.checked_hours,
      render: (v, r) => <Text strong type={r.status === 'OVER' ? 'danger' : (r.status === 'WARN' ? 'warning' : undefined)}>{v}</Text>,
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (v) => <Tag color={STATUS_META[v].color}>{STATUS_META[v].label}</Tag>,
    },
    { title: '평일 야근', dataIndex: 'weekday_overtime_hours', key: 'wd', width: 80, render: hoursCell },
    { title: '조근', dataIndex: 'early_overtime_hours', key: 'early', width: 60, render: hoursCell },
    { title: '특근', dataIndex: 'weekend_overtime_hours', key: 'we', width: 60, render: hoursCell },
    ...days.map((d, i) => ({
      title: (
        <span style={holidaySet.has(d) || i >= 5 ? { color: '#cf1322' } : undefined}>
          {WEEKDAY_LABELS[i]} {dayjs(d).format('M/D')}
        </span>
      ),
      key: `d${i}`,
      width: 70,
      render: (_, r) => hoursCell(r.daily_hours[i]),
    })),
  ], [days, holidaySet]);

  const deptColumns = [
    { title: '부서', dataIndex: 'part_name', key: 'part_name', width: 180, render: (v, r) => <Text strong>{v}{v !== r.part ? <Text type="secondary"> ({r.part})</Text> : null}</Text> },
    { title: '인원', dataIndex: 'headcount', key: 'headcount', width: 70 },
    { title: '평균 야근+조근', dataIndex: 'avg_hours', key: 'avg_hours', width: 120 },
    {
      title: '최대 야근+조근',
      dataIndex: 'max_hours',
      key: 'max_hours',
      width: 120,
      render: (v) => <Text strong type={v > (data?.limit_hours || 12) ? 'danger' : undefined}>{v}</Text>,
    },
    {
      title: '12시간 초과',
      dataIndex: 'over_count',
      key: 'over_count',
      width: 110,
      render: (v) => (v > 0 ? <Tag color="red">{v}명</Tag> : <Text type="secondary">0</Text>),
    },
    {
      title: '주의(10+)',
      dataIndex: 'warn_count',
      key: 'warn_count',
      width: 100,
      render: (v) => (v > 0 ? <Tag color="orange">{v}명</Tag> : <Text type="secondary">0</Text>),
    },
  ];

  // 보이는 부서의 재직 직원 전원 QR 카드 (링크는 서버에서 자동 생성)
  const handlePrintAllQr = async () => {
    setPrinting(true);
    try {
      const { data: res } = await api.get('/api/work-hours/links', { params: { part } });
      const list = (res.employees || []).filter((e) => e.token);
      if (!list.length) { message.info('인쇄할 직원이 없습니다.'); return; }
      const cards = await Promise.all(list.map(async (e) => ({
        name: e.name,
        partName: e.part_name,
        url: linkUrl(e.token),
        qrDataUrl: await QRCode.toDataURL(linkUrl(e.token), { width: 300, margin: 1 }),
      })));
      printQrCards(cards);
    } catch (e) {
      message.error(e.response?.data?.message || 'QR 일괄 인쇄 준비에 실패했습니다.');
    } finally {
      setPrinting(false);
    }
  };

  const handleExcel = async () => {
    if (!data) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('주간 근무시간');
    ws.addRow([`주간 근무시간 ${data.week_start} ~ ${data.week_end} (야근+조근 ${data.limit_hours}시간 초과 점검)`]);
    ws.addRow([]);
    const header = ['부서', '이름', '직급', '야근+조근', '상태', '평일 야근', '조근', '특근',
      ...days.map((d, i) => `${WEEKDAY_LABELS[i]} ${dayjs(d).format('M/D')}`)];
    const headerRow = ws.addRow(header);
    headerRow.font = { bold: true };
    data.departments.forEach((dept) => {
      dept.employees.forEach((e) => {
        const row = ws.addRow([
          dept.part_name, e.name, e.rank || '', e.checked_hours, STATUS_META[e.status].label,
          e.weekday_overtime_hours, e.early_overtime_hours, e.weekend_overtime_hours, ...e.daily_hours,
        ]);
        const argb = e.status === 'OVER' ? 'FFFFC7CE' : (e.status === 'WARN' ? 'FFFFEB9C' : null);
        if (argb) row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } }; });
      });
    });
    ws.columns.forEach((c, i) => { c.width = i < 2 ? 14 : 10; });
    const buffer = await wb.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buffer]), `주간근무시간_${data.week_start}.xlsx`);
  };

  const detailModal = (
    <EmployeeWorkHoursModal
      open={detailNo != null}
      employeeNo={detailNo}
      initialDate={weekStart.format('YYYY-MM-DD')}
      onClose={() => setDetailNo(null)}
    />
  );

  // 폰 화면은 한 줄 요약 목록형 전용 화면
  if (isMobile) {
    return (
      <ConfigProvider locale={ko_KR}>
        <WorkHoursMobile
          data={data}
          loading={loading}
          weekStart={weekStart}
          onChangeWeek={(d) => setWeekStart(mondayOf(d))}
          part={part}
          onChangePart={setPart}
          scope={scope}
          onRefresh={fetchData}
          onOpenEmployee={(e) => setDetailNo(e.employee_no)}
        />
        {detailModal}
      </ConfigProvider>
    );
  }

  const weekEnd = weekStart.add(6, 'day');

  return (
    <ConfigProvider locale={ko_KR}>
      <div className="common-container">
        <h1>주간 근무시간 (야근·조근 주 12시간)</h1>

        <Space wrap style={{ marginBottom: 16 }}>
          <Button icon={<LeftOutlined />} onClick={() => setWeekStart((w) => w.subtract(7, 'day'))} aria-label="이전 주" />
          <DatePicker
            value={weekStart}
            onChange={(d) => d && setWeekStart(mondayOf(d))}
            allowClear={false}
            format={() => `${weekStart.format('YYYY.MM.DD')}(월) ~ ${weekEnd.format('MM.DD')}(일)`}
            style={{ width: 230 }}
          />
          <Button icon={<RightOutlined />} onClick={() => setWeekStart((w) => w.add(7, 'day'))} aria-label="다음 주" />
          <Button onClick={() => setWeekStart(mondayOf(dayjs()))}>이번 주</Button>
          <Select value={part} onChange={setPart} style={{ width: 160 }}>
            <Option value="All">{scope && !scope.all ? '담당 부서 전체' : '전체 부서'}</Option>
            {(scope?.parts || []).map((p) => <Option key={p.part} value={p.part}>{p.part_name}</Option>)}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>새로고침</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExcel} disabled={!data}>엑셀 다운로드</Button>
          {canManageLink && (
            <Button icon={<QrcodeOutlined />} onClick={handlePrintAllQr} loading={printing} disabled={!data}>
              QR 일괄 인쇄
            </Button>
          )}
        </Space>

        {scope && !scope.all && scope.parts.length === 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="담당 부서가 지정되지 않았습니다."
            description="관리자에게 권한 관리 → 사용자 부서에서 담당 부서 지정을 요청하세요."
          />
        )}

        {data && (
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={8}><Card size="small"><Statistic title="대상 인원" value={data.summary.headcount} suffix="명" /></Card></Col>
            <Col xs={12} md={8}><Card size="small"><Statistic title="12시간 초과" value={data.summary.over_count} suffix="명" valueStyle={data.summary.over_count ? { color: '#cf1322' } : undefined} /></Card></Col>
            <Col xs={12} md={8}><Card size="small"><Statistic title="주의 (10시간 이상)" value={data.summary.warn_count} suffix="명" valueStyle={data.summary.warn_count ? { color: '#d48806' } : undefined} /></Card></Col>
          </Row>
        )}

        <Table
          rowKey="part"
          loading={loading}
          dataSource={data?.departments || []}
          columns={deptColumns}
          pagination={false}
          size="middle"
          tableLayout="fixed"
          scroll={{ x: 640 }}
          expandable={{
            expandedRowRender: (dept) => (
              <Table
                rowKey="employee_no"
                dataSource={dept.employees}
                columns={employeeColumns}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                rowClassName={(r) => (r.status === 'OVER' ? 'work-hours-over' : (r.status === 'WARN' ? 'work-hours-warn' : ''))}
              />
            ),
          }}
        />
        {detailModal}
      </div>
    </ConfigProvider>
  );
};

export default WorkHoursPage;
